import { useState } from "react";
import { CRITERIA_DISCIPLINES, criterionKey, effectiveCriterion } from "../../config/takeoffCriteria";
import { AHU_TYPES } from "../../utils/takeoffMath";

// Alternate WBS pivots over the Room Takeoff data. Same normalized state as
// the By-Room schedule (rooms, criteria KV, scope items, exclusions) — these
// are pure views with in-place editing:
//   DisciplineView — discipline sections, rooms as rows (sweep one trade)
//   SystemView     — AHU-centric rollups with per-zone room tables
//   GridView       — one flat sortable grid, every room × every field (QC)

const label11 = {
  fontSize: 11, fontWeight: 700, color: "var(--text3)",
  textTransform: "uppercase", letterSpacing: "0.05em",
};
const cellNumStyle = (hasOwn) => ({
  width: "100%", fontSize: 12, textAlign: "right", border: "1px solid var(--cs-border)",
  borderRadius: 5, padding: "4px 6px", background: "var(--surface)",
  color: hasOwn ? "var(--text)" : "var(--text3)",
});
const th = { ...label11, textAlign: "right", padding: "6px 8px", borderBottom: "1px solid var(--cs-border)", whiteSpace: "nowrap" };
const thLeft = { ...th, textAlign: "left" };
const fmt = (n) => (n === null || n === undefined || isNaN(n)) ? "—" : Number(n).toLocaleString();

function PerRoomCell({ disc, field, room, criteria, isLocked, onSaveCriterion }) {
  const own = criteria[criterionKey(disc.key, field.key, room.RoomID)] ?? "";
  const eff = effectiveCriterion(criteria, disc.key, field.key, room);
  return (
    <input
      type="number"
      min={0}
      defaultValue={own}
      disabled={isLocked}
      placeholder={eff.source && eff.source !== "override" ? eff.value : "—"}
      onBlur={e => { const v = e.target.value; if (v !== own) onSaveCriterion(disc.key, field.key, room.RoomID, v); }}
      style={cellNumStyle(!!own)}
    />
  );
}

// ── By Discipline ──────────────────────────────────────────────────────────────
export function DisciplineView({ rooms, criteria, scopeByDiscipline, scopeExclusions, isLocked, onSaveCriterion, onToggleScope }) {
  return (
    <div>
      {CRITERIA_DISCIPLINES.filter(d => !d.projectWide).map(disc => {
        const csItems = scopeByDiscipline[disc.scopeDiscipline] || [];
        const roomItems = csItems.filter(it => it.ScopeLevel === "room");
        const perRoomFields = disc.fields.filter(f => f.perRoom);
        const active = csItems.length > 0;

        return (
          <div key={disc.key} style={{ border: "1px solid var(--cs-border)", borderRadius: 8, marginBottom: 12, background: "var(--surface)", overflow: "hidden", opacity: active ? 1 : 0.6 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", borderBottom: "1px solid var(--cs-border)", background: "var(--surface2)" }}>
              <span style={{ fontSize: 15 }}>{disc.icon}</span>
              <span style={{ fontSize: 13, fontWeight: 700, color: "var(--text)" }}>{disc.label}</span>
              {!active && <span style={{ fontSize: 10.5, fontWeight: 700, padding: "1px 8px", borderRadius: 100, background: "var(--tint-amber)", color: "#92400e" }}>outside CS scope</span>}
              {/* Effective totals */}
              {perRoomFields.length > 0 && rooms.length > 0 && (
                <span style={{ marginLeft: "auto", fontSize: 11, color: "var(--text2)", fontVariantNumeric: "tabular-nums" }}>
                  {perRoomFields.map(f => {
                    const total = rooms.reduce((s, r) => s + (Number(effectiveCriterion(criteria, disc.key, f.key, r).value) || 0), 0);
                    return `${total.toLocaleString()} ${f.label.toLowerCase()}`;
                  }).join(" · ")}
                </span>
              )}
            </div>

            {/* Rooms × fields grid */}
            {perRoomFields.length > 0 && rooms.length > 0 && (
              <div style={{ overflowX: "auto" }}>
                <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 140 + perRoomFields.length * 110 }}>
                  <thead>
                    <tr style={{ background: "#fbfcfd" }}>
                      <th style={thLeft}>Room</th>
                      {perRoomFields.map(f => (
                        <th key={f.key} style={th} title={f.csi ? `CSI ${f.csi}` : ""}>{f.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rooms.map((room, i) => (
                      <tr key={room.RoomID} style={{ background: i % 2 ? "var(--surface2)" : "transparent" }}>
                        <td style={{ padding: "4px 8px", fontSize: 12, fontWeight: 600, color: "var(--text)", whiteSpace: "nowrap" }}>{room.RoomName}</td>
                        {perRoomFields.map(f => (
                          <td key={f.key} style={{ padding: "3px 5px" }}>
                            <PerRoomCell disc={disc} field={f} room={room} criteria={criteria} isLocked={isLocked} onSaveCriterion={onSaveCriterion} />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Scope applicability matrix: room-level items × rooms */}
            {roomItems.length > 0 && rooms.length > 0 && (
              <div style={{ padding: "10px 14px", borderTop: "1px solid var(--cs-border)" }}>
                <div style={{ ...label11, marginBottom: 6 }}>Scope applicability — ✓ applies to that room</div>
                <div style={{ overflowX: "auto" }}>
                  <table style={{ borderCollapse: "collapse", minWidth: 300 + rooms.length * 60 }}>
                    <thead>
                      <tr>
                        <th style={{ ...thLeft, borderBottom: "1px solid var(--cs-border)" }}>Scope item</th>
                        {rooms.map(r => (
                          <th key={r.RoomID} style={{ ...th, maxWidth: 90, overflow: "hidden", textOverflow: "ellipsis", textAlign: "center" }} title={r.RoomName}>{r.RoomName}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {roomItems.map((it, i) => (
                        <tr key={it.ScopeItemID} style={{ background: i % 2 ? "var(--surface2)" : "transparent" }}>
                          <td style={{ padding: "4px 8px", fontSize: 11.5, color: "var(--text2)", maxWidth: 340 }}>{it.Activity}</td>
                          {rooms.map(r => {
                            const excluded = scopeExclusions?.has(`${r.RoomID}|${it.ScopeItemID}`);
                            return (
                              <td key={r.RoomID} style={{ textAlign: "center", padding: "2px 4px" }}>
                                <input
                                  type="checkbox"
                                  checked={!excluded}
                                  disabled={isLocked}
                                  onChange={e => onToggleScope(r, it.ScopeItemID, !e.target.checked)}
                                />
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── By AHU / System ───────────────────────────────────────────────────────────
export function SystemView({ rooms, zones, zoneStats, criteria, isLocked, onSaveRoom, onSaveCriterion, roomTemplates = [] }) {
  const groups = [
    ...zones.map(z => ({ zone: z, stats: zoneStats.find(s => s.zone.AHUZoneID === z.AHUZoneID), rooms: rooms.filter(r => r.AHUZoneID === z.AHUZoneID) })),
    { zone: null, stats: null, rooms: rooms.filter(r => !r.AHUZoneID) },
  ].filter(g => g.zone !== null || g.rooms.length > 0);

  const mech = CRITERIA_DISCIPLINES.find(d => d.key === "mech");
  const exhaustField = mech.fields.find(f => f.key === "exhaust_cfm");

  return (
    <div>
      {groups.map(({ zone, stats, rooms: zr }, gi) => (
        <div key={zone?.AHUZoneID ?? "unassigned"} style={{ border: "1px solid var(--cs-border)", borderLeft: `3px solid ${zone ? "var(--cs-blue)" : "#d97706"}`, borderRadius: 8, marginBottom: 12, background: "var(--surface)", overflow: "hidden" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", borderBottom: "1px solid var(--cs-border)", background: "var(--surface2)", flexWrap: "wrap" }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: zone ? "var(--text)" : "#d97706" }}>
              {zone ? zone.ZoneName : "Unassigned rooms"}
            </span>
            {zone && <span style={{ fontSize: 11, color: "var(--text3)" }}>{AHU_TYPES.find(t => t.value === zone.AHUType)?.label}</span>}
            {stats && (
              <span style={{ marginLeft: "auto", fontSize: 11.5, color: "var(--text2)", display: "flex", gap: 12, fontVariantNumeric: "tabular-nums" }}>
                <span><strong>{fmt(stats.cfm)}</strong> CFM</span>
                <span>{fmt(stats.oaCfm)} OA</span>
                <span>{fmt(stats.hepas)} HEPAs</span>
                {stats.returnDia && <span>{stats.returnDia}&Prime; return</span>}
              </span>
            )}
          </div>
          {zr.length === 0 ? (
            <div style={{ padding: "10px 14px", fontSize: 12, color: "var(--text3)" }}>No rooms assigned.</div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 700 }}>
                <thead>
                  <tr style={{ background: "#fbfcfd" }}>
                    <th style={thLeft}>Room</th>
                    <th style={thLeft}>Template</th>
                    {["L", "W", "H", "ACH"].map(h => <th key={h} style={th}>{h}</th>)}
                    <th style={th}>CFM</th>
                    <th style={th} title={exhaustField.csi ? `CSI ${exhaustField.csi}` : ""}>Exhaust CFM</th>
                  </tr>
                </thead>
                <tbody>
                  {zr.map((room, i) => (
                    <tr key={room.RoomID} style={{ background: i % 2 ? "var(--surface2)" : "transparent" }}>
                      <td style={{ padding: "4px 8px", fontSize: 12, fontWeight: 600, whiteSpace: "nowrap" }}>{room.RoomName}</td>
                      <td style={{ padding: "4px 8px", fontSize: 11.5, color: "var(--text2)", whiteSpace: "nowrap" }}>{roomTemplates.find(t => t.RoomTemplateID === room.RoomTemplateID)?.Name || "—"}</td>
                      {["LengthFt", "WidthFt", "HeightFt", "ACH"].map(k => (
                        <td key={k} style={{ padding: "3px 5px", width: 66 }}>
                          <input
                            type="number" min={0}
                            defaultValue={room[k] ?? ""}
                            disabled={isLocked}
                            onBlur={e => { const v = e.target.value === "" ? null : +e.target.value; if (v !== (room[k] ?? null)) onSaveRoom({ ...room, [k]: v }); }}
                            style={cellNumStyle(true)}
                          />
                        </td>
                      ))}
                      <td style={{ padding: "4px 8px", fontSize: 12, textAlign: "right", color: "var(--text2)", fontVariantNumeric: "tabular-nums" }}>
                        {room._cfm != null ? fmt(room._cfm) : "—"}
                      </td>
                      <td style={{ padding: "3px 5px", width: 92 }}>
                        <PerRoomCell disc={mech} field={exhaustField} room={room} criteria={criteria} isLocked={isLocked} onSaveCriterion={onSaveCriterion} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ── Flat grid ─────────────────────────────────────────────────────────────────
// A project-wide (select/number, not perRoom) field has one value for the
// whole project — edited only in the frozen "Project Default" row. Room rows
// show a muted dash for it rather than repeating the same string 40+ times.
function ProjectDefaultCell({ disc, field, criteria, isLocked, onSaveCriterion }) {
  const v = criteria[criterionKey(disc.key, field.key, null)] ?? "";
  if (field.type === "select") {
    return (
      <select
        value={v}
        disabled={isLocked}
        onChange={e => onSaveCriterion(disc.key, field.key, null, e.target.value)}
        style={{ width: "100%", fontSize: 11.5, border: "1px solid var(--cs-border)", borderRadius: 5, padding: "4px 5px", background: "var(--surface)", color: v ? "var(--text)" : "var(--text3)" }}
      >
        <option value="">— not set —</option>
        {field.options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }
  return (
    <input
      type="number" min={0}
      defaultValue={v}
      disabled={isLocked}
      placeholder="—"
      onBlur={e => { const nv = e.target.value; if (nv !== v) onSaveCriterion(disc.key, field.key, null, nv); }}
      style={cellNumStyle(!!v)}
    />
  );
}

export function GridView({ rooms, criteria, isLocked, onSaveRoom, onSaveCriterion, roomTemplates = [], onChangeRoomTemplate }) {
  const [sort, setSort] = useState({ key: null, dir: 1 });

  // Every field across every discipline (including projectWide-only
  // disciplines like Structural/Fire Protection), in discipline order —
  // this is the whole "huge spreadsheet", not just the per-room subset.
  const allCols = CRITERIA_DISCIPLINES.flatMap(d => d.fields.map(f => ({ disc: d, field: f })));

  const valueOf = (room, key) => {
    if (key === "name") return room.RoomName || "";
    if (key === "template") return roomTemplates.find(t => t.RoomTemplateID === room.RoomTemplateID)?.Name || "";
    if (["LengthFt", "WidthFt", "HeightFt", "ACH"].includes(key)) return Number(room[key]) || 0;
    const col = allCols.find(c => c.field.key === key && c.field.perRoom);
    if (col) return Number(effectiveCriterion(criteria, col.disc.key, col.field.key, room).value) || 0;
    return 0;
  };

  const sorted = sort.key
    ? [...rooms].sort((a, b) => {
        const va = valueOf(a, sort.key), vb = valueOf(b, sort.key);
        return (typeof va === "string" ? va.localeCompare(vb) : va - vb) * sort.dir;
      })
    : rooms;

  const sortable = (key, label, alignLeft) => (
    <th
      key={key}
      onClick={() => setSort(s => ({ key, dir: s.key === key ? -s.dir : 1 }))}
      style={{ ...(alignLeft ? thLeft : th), cursor: "pointer", userSelect: "none", background: sort.key === key ? "#eef2f7" : undefined }}
      title="Click to sort"
    >
      {label}{sort.key === key ? (sort.dir === 1 ? " ▲" : " ▼") : ""}
    </th>
  );

  const colWidth = (field) => field.type === "select" ? 150 : 88;

  return (
    <div style={{ border: "1px solid var(--cs-border)", borderRadius: 8, overflowX: "auto", background: "var(--surface)" }}>
      <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 560 + allCols.reduce((s, c) => s + colWidth(c.field), 0) }}>
        <thead>
          {/* Discipline group header */}
          <tr>
            <th colSpan={6} style={{ borderBottom: "1px solid var(--cs-border)" }} />
            {CRITERIA_DISCIPLINES.map(d => (
              <th key={d.key} colSpan={d.fields.length} style={{ ...label11, padding: "5px 8px", borderBottom: "1px solid var(--cs-border)", borderLeft: "2px solid var(--cs-border)", textAlign: "center", background: "var(--surface2)" }}>
                {d.icon} {d.label}
              </th>
            ))}
          </tr>
          <tr style={{ background: "#fbfcfd" }}>
            {sortable("name", "Room", true)}
            {sortable("template", "Template", true)}
            {sortable("LengthFt", "L")}
            {sortable("WidthFt", "W")}
            {sortable("HeightFt", "H")}
            {sortable("ACH", "ACH")}
            {allCols.map(({ disc, field }, i) => {
              const firstOfDisc = i === 0 || allCols[i - 1].disc.key !== disc.key;
              const borderLeft = firstOfDisc ? "2px solid var(--cs-border)" : undefined;
              return field.perRoom ? (
                <th
                  key={field.key}
                  onClick={() => setSort(s => ({ key: field.key, dir: s.key === field.key ? -s.dir : 1 }))}
                  style={{ ...th, cursor: "pointer", userSelect: "none", borderLeft, background: sort.key === field.key ? "#eef2f7" : undefined }}
                  title={`${field.label}${field.csi ? ` — CSI ${field.csi}` : ""} · click to sort`}
                >
                  {field.label}{sort.key === field.key ? (sort.dir === 1 ? " ▲" : " ▼") : ""}
                </th>
              ) : (
                <th key={field.key} style={{ ...th, borderLeft }} title={`${field.label}${field.csi ? ` — CSI ${field.csi}` : ""} — project-wide, set in the Project Default row below`}>
                  {field.label}
                </th>
              );
            })}
          </tr>
          {/* Frozen project-default row — the one place project-wide fields are edited */}
          <tr style={{ background: "#eef2f7" }}>
            <td colSpan={6} style={{ ...label11, padding: "6px 8px" }}>Project Default</td>
            {allCols.map(({ disc, field }, i) => {
              const firstOfDisc = i === 0 || allCols[i - 1].disc.key !== disc.key;
              return (
                <td key={field.key} style={{ padding: "3px 5px", width: colWidth(field), borderLeft: firstOfDisc ? "2px solid var(--cs-border)" : undefined }}>
                  {field.perRoom ? (
                    <PerRoomCell disc={disc} field={field} room={{ RoomID: null }} criteria={criteria} isLocked={isLocked} onSaveCriterion={onSaveCriterion} />
                  ) : (
                    <ProjectDefaultCell disc={disc} field={field} criteria={criteria} isLocked={isLocked} onSaveCriterion={onSaveCriterion} />
                  )}
                </td>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {sorted.map((room, i) => (
            <tr key={room.RoomID} style={{ background: i % 2 ? "var(--surface2)" : "transparent" }}>
              <td style={{ padding: "4px 8px", fontSize: 12, fontWeight: 600, whiteSpace: "nowrap" }}>{room.RoomName}</td>
              <td style={{ padding: "3px 5px" }}>
                <select
                  value={room.RoomTemplateID ?? ""}
                  disabled={isLocked}
                  onChange={e => onChangeRoomTemplate?.(room, e.target.value ? +e.target.value : null)}
                  style={{ fontSize: 11.5, border: "1px solid var(--cs-border)", borderRadius: 5, padding: "3px 4px", background: "var(--surface)", color: room.RoomTemplateID ? "var(--text)" : "var(--text3)", maxWidth: 140 }}
                >
                  <option value="">— none —</option>
                  {roomTemplates.filter(t => t.IsActive || t.RoomTemplateID === room.RoomTemplateID).map(t => (
                    <option key={t.RoomTemplateID} value={t.RoomTemplateID}>{t.Name}</option>
                  ))}
                </select>
              </td>
              {["LengthFt", "WidthFt", "HeightFt", "ACH"].map(k => (
                <td key={k} style={{ padding: "3px 5px", width: 62 }}>
                  <input
                    type="number" min={0}
                    defaultValue={room[k] ?? ""}
                    disabled={isLocked}
                    onBlur={e => { const v = e.target.value === "" ? null : +e.target.value; if (v !== (room[k] ?? null)) onSaveRoom({ ...room, [k]: v }); }}
                    style={cellNumStyle(true)}
                  />
                </td>
              ))}
              {allCols.map(({ disc, field }, ci) => {
                const firstOfDisc = ci === 0 || allCols[ci - 1].disc.key !== disc.key;
                const borderLeft = firstOfDisc ? "2px solid var(--cs-border)" : undefined;
                return (
                  <td key={field.key} style={{ padding: "3px 5px", width: colWidth(field), borderLeft }}>
                    {field.perRoom ? (
                      <PerRoomCell disc={disc} field={field} room={room} criteria={criteria} isLocked={isLocked} onSaveCriterion={onSaveCriterion} />
                    ) : (
                      <div title="Project-wide setting — edit in the Project Default row above" style={{ padding: "4px 6px", fontSize: 11.5, textAlign: "center", color: "var(--text3)" }}>
                        —
                      </div>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
          {/* Effective totals */}
          <tr style={{ background: "var(--surface2)", borderTop: "2px solid var(--cs-border)" }}>
            <td colSpan={6} style={{ ...label11, padding: "6px 8px" }}>Total (effective)</td>
            {allCols.map(({ disc, field }, ci) => {
              const firstOfDisc = ci === 0 || allCols[ci - 1].disc.key !== disc.key;
              const borderLeft = firstOfDisc ? "2px solid var(--cs-border)" : undefined;
              if (!field.perRoom) return <td key={field.key} style={{ borderLeft }} />;
              const total = rooms.reduce((s, r) => s + (Number(effectiveCriterion(criteria, disc.key, field.key, r).value) || 0), 0);
              return (
                <td key={field.key} style={{ padding: "6px 8px", fontSize: 12, fontWeight: 700, textAlign: "right", fontVariantNumeric: "tabular-nums", borderLeft }}>
                  {total.toLocaleString()}
                </td>
              );
            })}
          </tr>
        </tbody>
      </table>
    </div>
  );
}
